import {
  ChangeDetectionStrategy,
  Component,
  input,
  InputSignal,
  OnInit,
  output,
  signal,
  viewChild,
  inject,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActionButton } from '@shared/abstraction/grid/action-button';
import { SearchSetting } from '@shared/abstraction/grid/search-setting';
import { DataType } from '@shared/helpers/enums/data-type';
import { QueryOptions } from 'odata-query';
import {
  Collection,
  GridItemCreateEvent,
  GridItemUpdateEvent,
  SecurityType,
} from '../../models';
import { AdminPrimeGridComponent } from '../admin-prime-grid/admin-prime-grid.component';
import { UpsertSecurityTypeComponent } from '../upsert-security-type/upsert-security-type.component';
import { DrawerSetting, GridColumn } from '@shared/modules/grid/abstractions';

@Component({
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'gstar-security-types-grid',
  templateUrl: './security-types-grid.component.html',
})
export class SecurityTypesGridComponent implements OnInit {
  private translateService = inject(TranslateService);

  source: InputSignal<Collection<SecurityType>> = input.required({});

  query = input<Partial<QueryOptions<SecurityType>>>();
  queryWritable = signal<Partial<QueryOptions<SecurityType>>>({});

  queryChange = output<Partial<QueryOptions<SecurityType>>>();
  create = output<GridItemCreateEvent<SecurityType>>();
  update = output<GridItemUpdateEvent<SecurityType>>();

  actionMenuTitle = this.translateService.instant(
    'admin.grid.actionsColumnName'
  );
  drawerSetting: DrawerSetting<SecurityType> = {
    create: {
      title: this.translateService.instant(
        'admin.grid.createSecurityTypeTitle'
      ),
      component: UpsertSecurityTypeComponent,
      buttons: [
        {
          label: this.translateService.instant('admin.grid.cancel'),
          command: (closeFn): void => {
            closeFn();
          },
          isPrimary: false,
        },
        {
          label: this.translateService.instant('admin.grid.create'),
          command: (closeFn, item): void => {
            if (item?.isValid) {
              this.create.emit({ item: item.model!, onSuccess: closeFn });
            }
          },
          isPrimary: true,
        },
      ],
    },
    edit: {
      title: this.translateService.instant(
        'admin.grid.updateSecurityTypeTitle'
      ),
      component: UpsertSecurityTypeComponent,
      buttons: [
        {
          label: this.translateService.instant('admin.grid.cancel'),
          command: (closeFn): void => {
            closeFn();
          },
          isPrimary: false,
        },
        {
          label: this.translateService.instant('admin.grid.update'),
          command: (closeFn, item): void => {
            if (item.isValid) {
              this.update.emit({
                item: item.model!,
                prevItem: item.prevModel,
                onSuccess: closeFn,
              });
            }
          },
          isPrimary: true,
        },
      ],
    },
  };

  searchSetting: SearchSetting<SecurityType> = {
    filtersLogicalOperator: 'or',
    filters: [
      {
        dataKey: 'name',
        operator: 'contains',
      },
    ],
  };
  grid = viewChild<AdminPrimeGridComponent<SecurityType, SecurityType>>('grid');

  actionMenu: ActionButton<SecurityType>[] = [
    {
      label: this.translateService.instant('portfolio.actionMenu.edit'),
      icon: '',
      command: (item: SecurityType): void => {
        this.grid()?.editActionClick(item);
      },
    },
  ];

  headers: GridColumn[] = [
    {
      label: this.translateService.instant('securityType.name'),
      key: 'name',
      dataType: DataType.String,
      isSortable: true,
    },
    {
      label: this.translateService.instant('securityType.baseSecurityType'),
      key: 'parentName',
      sortKey: 'parentSecurityType/name',
      dataType: DataType.String,
      isSortable: true,
    },
    {
      label: this.translateService.instant('securityType.created'),
      key: 'created',
      dataType: DataType.Date,
      isSortable: true,
    },
    {
      label: this.translateService.instant('securityType.isActive'),
      key: 'isActive',
      dataType: DataType.Boolean,
      isSortable: true,
    },
  ];

  ngOnInit(): void {
    this.queryWritable.set(this.query() ?? {});
  }

  queryHandler(securityTypeQuery: Partial<QueryOptions<SecurityType>>): void {
    this.queryWritable.update(
      (previousQuery: Partial<QueryOptions<SecurityType>>) => {
        return {
          ...previousQuery,
          ...securityTypeQuery,
        };
      }
    );
    this.queryChange.emit(this.queryWritable());
  }
}
